import React from "react";
import { useForm } from "react-hook-form";
import { StateSelect } from "../../shared/StateSelect/StateSelect";
import { TextField } from "../../shared/TextField/TextField";
import { Select } from "../../shared/Select/Select";
import { Radio } from "../../shared/Radio/Radio";

export interface CredentialsFormData {
  practiceType: "solo" | "group" | "clinic";
  role: string;
  license: {
    number: string;
    state: string;
    expiration: string;
  };
}

interface CredentialsStepProps {
  onNext: (data: CredentialsFormData) => void;
  onBack: () => void;
  initialData?: CredentialsFormData;
}

const mentalHealthRoles = [
  {
    value: "Licensed Professional Counselor (LPC)",
    label: "Licensed Professional Counselor (LPC)",
  },
  {
    value: "Licensed Clinical Social Worker (LCSW)",
    label: "Licensed Clinical Social Worker (LCSW)",
  },
  {
    value: "Licensed Marriage and Family Therapist (LMFT)",
    label: "Licensed Marriage and Family Therapist (LMFT)",
  },
  {
    value: "Licensed Mental Health Counselor (LMHC)",
    label: "Licensed Mental Health Counselor (LMHC)",
  },
  { value: "Psychologist", label: "Psychologist" },
  { value: "Psychiatrist", label: "Psychiatrist" },
  { value: "Clinical Psychologist", label: "Clinical Psychologist" },
];

export const CredentialsStep: React.FC<CredentialsStepProps> = ({
  onNext,
  onBack,
  initialData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CredentialsFormData>({
    defaultValues: initialData,
  });

  const practiceType = watch("practiceType");

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      <div>
        <label className="text-base font-medium text-gray-900">
          Practice Type
        </label>
        <p className="text-sm text-gray-500">
          Select the type of practice you operate
        </p>
        <div className="mt-4 space-y-4">
          <Radio
            {...register("practiceType")}
            isChecked={practiceType === "solo"}
            id="solo"
            value="solo"
            label="Solo Practice"
          />

          <Radio
            {...register("practiceType")}
            isChecked={practiceType === "group"}
            id="group"
            value="group"
            label="Group Practice"
          />

          <Radio
            {...register("practiceType")}
            isChecked={practiceType === "clinic"}
            id="clinic"
            value="clinic"
            label="Mental Health Clinic"
          />
        </div>
      </div>

      <Select
        {...register("role", { required: "Professional Role is required" })}
        label="Professional Role"
        id="professionalRole"
        options={mentalHealthRoles}
        errorMessage={errors.role?.message}
      />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            {...register("license.number", {
              required: "License Number is required",
              pattern: {
                value: /^[A-Za-z0-9-]+$/,
                message: "Please enter a valid license number",
              },
            })}
            label="License Number"
            id="licenseNumber"
            errorMessage={errors.license?.number?.message}
          />
        </div>

        <div>
          <StateSelect
            id="licenseState"
            label="License State"
            {...register("license.state", {
              required: "License state is required",
            })}
            errorMessage={errors.license?.state?.message}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            License Expiration
          </label>
          <input
            type="date"
            {...register("license.expiration", {
              required: "License expiration is required",
              validate: (value) =>
                new Date(value) > new Date() || "License must not be expired",
            })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
          />
          {errors.license?.expiration && (
            <p className="mt-1 text-sm text-red-600">
              {errors.license.expiration.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Back
        </button>
        <button
          type="submit"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Next Step
        </button>
      </div>
    </form>
  );
};
